import { BannerHero } from 'components/banner/bannerHero';
import { BannerLandscape } from 'components/banner/bannerLandscape';
import { SuggestionCarousel } from 'components/carousels/suggestionCarousel';
import { HeaderTitle } from 'components/headerTitle';
import { DynamicImage } from 'components/Image';
import { InternalMenu } from 'components/internalMenu';
import { LearnMore } from 'components/learnMore';
import { LotteryModal } from 'components/modals/lotteryModal';
import { Skeleton } from 'components/skeleton';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import type { ThemeLogoType } from 'components/themeLogo';
import { VisualComposer } from 'components/visualComposer';
import { createPageComposition } from 'components/visualComposer/utils/utils';
import { Wrapper } from 'components/wrapper';
import { useGetMightLikesQuery, useGetQuickSlotQuery } from 'features/api/apiSlice';
import { useGetPageBannersQuery } from 'features/api/promoEngineApiSlice';
import { setIsOpenLotteryModalValue } from 'features/modal/modalSlice';
import { useGetMightLikesByList } from 'hooks/useGetAllMightLikes';
import { useInternalSession } from 'hooks/useInternalSession';
import { useGetLabelByKey, useGetLabelsByKeys } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { BANNER_ON_PAGE_CMS, BANNER_ON_PAGE_PROMO, HERO_BANNER_CMS, HERO_BANNER_PROMO } from 'lib/datoCms/constants';
import { DatoCmsImageTheme } from 'lib/datoCms/types';
import { convertInThemeLogoType, handleBanner } from 'lib/datoCms/utils';
import { NextPage } from 'next';
import React, { useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { getLottery, getWindowFeature } from 'sites/snai/features/lotterie/actions';
import { conditionsToVisualizeBanner, getContrastedTextColor, isNotEmpty } from 'utility/functions';
import { PageContentProps, Suppliers } from '.';
import styles from './LotterieSlug.module.scss';

export const PageContent: NextPage<PageContentProps> = ({ lotteriesPageCategory }) => {
  const {
    id,
    slug,
    supplier,
    title,
    heroBanner,
    internalMenu,
    learnMore,
    visualComposer,
    tabsWithAccordion,
    logo,
    description,
    scripts,
  } = lotteriesPageCategory ?? {};
  const labelGioca = useGetLabelByKey('gioca');
  const { supplierLogo, supplierType, backgroundColor } = supplier ?? {};

  const dispatch = useAppDispatch();

  const { isMobile } = useWindowSize();

  // STATO REDUX PER MODALE
  const isOpenLotteryModal = useTypedSelector(({ modal }) => modal.isOpenLotteryModal);

  // CUSTOM HOOK
  const [labelLotterie] = useGetLabelsByKeys(['lotterie']);
  const { session, isAuthenticated } = useInternalSession();
  const { user } = session ?? {};

  // QUERY PROMO ENGINE
  const { isError: isErrorPromoEngine, data: promoEngine } = useGetPageBannersQuery(id);

  // GET MIGHT LIKES
  const { data: mightLikesResposeApi } = useGetMightLikesQuery(slug);

  let mightLikesList = useGetMightLikesByList(mightLikesResposeApi?.cardList);

  // GET QUICK SLOT
  const { listQuickSlot, quickSlotIsLoading, isUninitializedQuickSlot } = useGetQuickSlotQuery(undefined, {
    selectFromResult: ({ data, isError, isLoading, isUninitialized }) => ({
      listQuickSlot: data?.games ?? [],
      isErrorQuickSlot: isError,
      quickSlotIsLoading: isLoading,
      isUninitializedQuickSlot: isUninitialized,
    }),
  });

  const newVisualComposer = useMemo(() => {
    const catalogResponseApi = { listQuickSlot, games: [] };

    return (
      visualComposer &&
      catalogResponseApi &&
      createPageComposition<typeof listQuickSlot>({
        visualComposer: visualComposer!,
        catalog: catalogResponseApi,
        slug: slug,
      }).map((e) => e!)
    );
  }, [listQuickSlot, slug, visualComposer]);

  const supplierLogoList = useMemo(
    () =>
      supplierLogo?.supplierLogoLottery
        ?.map((supplierLogo: DatoCmsImageTheme) => {
          return convertInThemeLogoType(supplierLogo);
        })
        .filter((x) => !!x) as Array<ThemeLogoType>,
    [supplierLogo?.supplierLogoLottery]
  );

  const handleLaunchLottery = useCallback(async () => {
    const w = window.open(``, '_blank', getWindowFeature())!;
    try {
      const payload = await dispatch(getLottery({ supplier, slug, isMobile, window: w })).unwrap();

      if (payload) {
        w.location = payload;
        return;
      }
    } catch (error) {
      console.error('Error launching lottery:', error);
    }
  }, [isMobile, slug, supplier, dispatch]);

  // handle of the opening of the registration modal and token generation
  useEffect(() => {
    if (!user) return;

    const supplierContracts: Record<string, string | undefined> = {
      [Suppliers.GrattaVinci]: user.contrattoGrattaeVinci,
      [Suppliers.Sisal]: user.contrattoSisal,
      [Suppliers.Lottomatica]: user.contrattoLottomatica,
    };

    const contractValue = supplierContracts[supplierType];

    if (contractValue === '0') {
      dispatch(setIsOpenLotteryModalValue(true));
    }
  }, [user, supplierType, isAuthenticated, dispatch]);

  if (isUninitializedQuickSlot || quickSlotIsLoading) {
    return <Skeleton rows={1} />;
  }

  // if (isErrorQuickSlot) {
  //   return <p>{apiGenericErrorLabel}</p>;
  // }

  return (
    <React.Fragment>
      {isOpenLotteryModal && isAuthenticated && (
        <LotteryModal
          title={title}
          isOpen={isOpenLotteryModal}
          supplier={supplier?.supplierType}
          lotteryType="secondary"
          supplierLogo={supplierLogoList}
        />
      )}
      {title && <HeaderTitle title={labelLotterie} titleHighlighted={title}></HeaderTitle>}
      {conditionsToVisualizeBanner(lotteriesPageCategory, {
        type: HERO_BANNER_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      }) && <BannerHero banners={promoEngine!.heroBannerList} />}
      {conditionsToVisualizeBanner(lotteriesPageCategory, {
        type: HERO_BANNER_CMS,
      }) && <BannerHero banners={handleBanner(heroBanner[0].banners!)} />}
      {internalMenu && <InternalMenu items={internalMenu[0].linkList} />}
      <Wrapper>
        <div className={styles.wrapper} style={{ backgroundColor: backgroundColor?.hex }}>
          <div className={styles.container}>
            {isNotEmpty(logo?.url) && (
              <div className={styles.image}>
                <DynamicImage
                  src={`${logo?.url}`}
                  title={logo?.title ?? ''}
                  alt={logo?.alt ?? ''}
                  fill
                  loading="eager"
                  decoding="async"
                  className={styles.objectFit}
                />
              </div>
            )}
            <div className={styles.descriptionContainer}>
              <span className={styles.description} style={{ color: getContrastedTextColor(backgroundColor.hex) }}>
                {description}
              </span>
              <button className={styles.cta} onClick={() => handleLaunchLottery()}>
                {labelGioca}
              </button>
            </div>
          </div>
        </div>
      </Wrapper>
      {newVisualComposer && <VisualComposer type="lotteries" components={newVisualComposer} />}
      {conditionsToVisualizeBanner(lotteriesPageCategory, {
        type: BANNER_ON_PAGE_CMS,
      }) && <BannerLandscape data={handleBanner(lotteriesPageCategory!.bannerOnPage![0].banners!)[0]} />}
      {conditionsToVisualizeBanner(lotteriesPageCategory, {
        type: BANNER_ON_PAGE_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      }) && <BannerLandscape data={promoEngine!.pageBannerList[0]} />}
      {mightLikesList && mightLikesList.length > 0 && <SuggestionCarousel listItem={mightLikesList} />}
      {learnMore && <LearnMore title={learnMore.title} links={learnMore.links} />}
      {tabsWithAccordion && <TabsWithAccordion {...tabsWithAccordion} />}
    </React.Fragment>
  );
};
